import {
  IonBreadcrumb,
  IonBreadcrumbs,
  IonContent,
  IonPage,
  IonRow,
  useIonViewWillEnter
} from '@ionic/react';
import SearchBar, { filterItems } from 'components/searchbar/SearchBar';
import BackButtonHeader from 'core/layout/BackButtonHeader';
import LoadingIonList from 'core/layout/loading-ion-list/LoadingIonList';
import {
  HKPlatform,
  INVENTORY_COLORS_BREADCRUMB_LABEL,
  INVENTORY_COLORS_PRODUCT_GROUP_ID,
  INVENTORY_OPTIONS_BREADCRUMB_LABEL,
  INVENTORY_OPTIONS_PRODUCT_GROUP_ID
} from 'core/constants';
import type { RootState } from 'core/store';
import type { InventoryItem } from 'core/types';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import {
  requestProductGroup,
  requestProductGroupCount
} from 'features/shared/inventory/store/InventoryActions';
import InventorySelectionAccordionGroup from 'features/shared/inventory/selections/components/InventorySelectionAccordionGroup';
import { groupInventoryItems } from 'features/shared/inventory/selections/utils/groupInventoryItems';
import ColorSelectionListRowMobile from 'features/shared/inventory/selections/color-selections/components/ColorSelectionListRowMobile';
import ColorSelectionListRowDesktop, {
  inventoryItemToColorSelectionRowProps
} from 'features/shared/inventory/selections/color-selections/components/ColorSelectionListRowDesktop';
import HomeSelectionListRowDesktop, {
  inventoryItemToHomeSelectionRowProps
} from 'features/shared/inventory/selections/home-selections/components/HomeSelectionListRowDesktop';
import HomeSelectionListRowMobile from 'features/shared/inventory/selections/home-selections/components/HomeSelectionListRowMobile';
import 'features/shared/inventory/InventoryGroups.scss';
import 'features/shared/inventory/InventoryProducts.scss';

const OPTIONS_GROUP_ID = INVENTORY_OPTIONS_PRODUCT_GROUP_ID;
const COLORS_GROUP_ID = INVENTORY_COLORS_PRODUCT_GROUP_ID;

/**
 * Home Option Selections (`/inventory/group/67`) and Home Color Selections
 * (`/inventory/group/68`) — two separate top-level pages (RHC10-15411),
 * reached directly from their own cards on `/inventory` rather than one
 * nesting a click-through into the other.
 */
const InventoryHomeSelections: React.FC = () => {
  const { products, productGroupLoading } = useSelector(
    (state: RootState) => state.inventory
  );
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );
  const [search, setSearch] = useState('');
  const location = useLocation();
  const dispatch = useDispatch();

  const listGroupId = useMemo(() => {
    const match = location.pathname.match(/^\/inventory\/group\/(\d+)$/);
    if (!match) {
      return OPTIONS_GROUP_ID;
    }
    return Number(match[1]);
  }, [location.pathname]);

  const isColorsListPage = listGroupId === COLORS_GROUP_ID;
  const isDesktop = platformType === HKPlatform.DESKTOP && isDesktopWidth;
  const pageTitle = isColorsListPage
    ? INVENTORY_COLORS_BREADCRUMB_LABEL
    : INVENTORY_OPTIONS_BREADCRUMB_LABEL;

  const filteredProducts = useMemo(
    () =>
      filterItems(
        products || [],
        [
          'product_template.manufacturer_name',
          'product_template.type',
          'product_template.model',
          'name',
          'serial',
          'attributes.colorgroupdesc',
          'attributes.colordesc'
        ],
        search
      ),
    [products, search]
  );

  // Grouped by `optionCategory`, per the approved prototype — items missing
  // a category fall into a trailing "No Category" section.
  const optionGroups = useMemo(
    () =>
      groupInventoryItems(
        filteredProducts as InventoryItem[],
        (item) => item.attributes?.optioncategory,
        (key) =>
          key === null
            ? { label: 'No Category' }
            : { label: `Option Group ${key}` }
      ),
    [filteredProducts]
  );

  // Grouped by `colorGroupCode` — the group label is its `colorGroupDesc`
  // (from the first item in the group).
  const colorGroups = useMemo(
    () =>
      groupInventoryItems(
        filteredProducts as InventoryItem[],
        (item) => item.attributes?.colorgroupcode,
        (key, itemsInGroup) =>
          key === null
            ? { label: 'No Color Group' }
            : { label: itemsInGroup[0]?.attributes?.colorgroupdesc ?? key }
      ),
    [filteredProducts]
  );

  useIonViewWillEnter(() => {
    if (!homeId) {
      return;
    }
    dispatch(requestProductGroupCount(homeId));
    dispatch(requestProductGroup(homeId, String(listGroupId)));
  }, [dispatch, homeId, listGroupId]);

  function renderOptionCards() {
    return (
      <InventorySelectionAccordionGroup
        groups={optionGroups}
        renderRow={(product: InventoryItem, i: number) => {
          const rowProps = inventoryItemToHomeSelectionRowProps(product);
          return isDesktop ? (
            <HomeSelectionListRowDesktop
              key={`home-selection-option-${product.id}-${i}`}
              {...rowProps}
            />
          ) : (
            <HomeSelectionListRowMobile
              key={`home-selection-option-${product.id}-${i}`}
              {...rowProps}
            />
          );
        }}
      />
    );
  }

  function renderColorCards() {
    return (
      <InventorySelectionAccordionGroup
        groups={colorGroups}
        renderRow={(product: InventoryItem, i: number) => {
          const rowProps = inventoryItemToColorSelectionRowProps(product);
          return isDesktop ? (
            <ColorSelectionListRowDesktop
              key={`home-selection-color-${product.id}-${i}`}
              {...rowProps}
            />
          ) : (
            <ColorSelectionListRowMobile
              key={`home-selection-color-${product.id}-${i}`}
              {...rowProps}
            />
          );
        }}
      />
    );
  }

  function DesktopView() {
    return (
      <>
        <IonRow className="hk-desktop-digital-inventory-products-header">
          <div className="hk-desktop-digital-inventory-products-header-container">
            <IonBreadcrumbs className="hk-desktop-digital-inventory-products-header-breadcrumbs">
              <IonBreadcrumb href="/inventory">Inventory</IonBreadcrumb>
              <IonBreadcrumb>{pageTitle}</IonBreadcrumb>
            </IonBreadcrumbs>
            <div className="hk-desktop-digital-inventory-products-header-right">
              <div className="hk-desktop-digital-inventory-products-search">
                <SearchBar
                  value={search}
                  disabled={!products?.length}
                  onSearchChange={(e) => setSearch(e)}
                />
              </div>
            </div>
          </div>
        </IonRow>
        <IonContent className="hk-desktop-digital-inventory-products">
          <LoadingIonList
            className="hk-desktop-digital-inventory-products-list"
            label={'Items In Inventory'}
            description="There are no items of this kind added yet."
            loading={productGroupLoading}
            items={filteredProducts}
          >
            {isColorsListPage ? renderColorCards() : renderOptionCards()}
          </LoadingIonList>
        </IonContent>
      </>
    );
  }

  function MobileView() {
    return (
      <>
        <BackButtonHeader title={pageTitle} />
        <IonContent fullscreen>
          <SearchBar
            value={search}
            disabled={!products?.length}
            onSearchChange={(e) => setSearch(e)}
          />
          <LoadingIonList
            label={'Items In Inventory'}
            description="There are no items of this kind added yet."
            loading={productGroupLoading}
            items={filteredProducts}
            className="hk-digital-inventory-products-list"
          >
            {isColorsListPage ? renderColorCards() : renderOptionCards()}
          </LoadingIonList>
        </IonContent>
      </>
    );
  }

  return (
    <IonPage
      className={isDesktop ? 'hk-desktop-digital-inventory-products-page' : ''}
    >
      {isDesktop ? <>{DesktopView()}</> : MobileView()}
    </IonPage>
  );
};

export default InventoryHomeSelections;
